import { castArray, isString } from 'lodash';
import type { search } from 'sr-types';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import type { I18nFunctionType } from '../common/i18n/I18n';
import { getContactNameFromObject } from '../common/utils/nameUtils';
import type { BryntumProductionRecord, BryntumResourceRecord } from './planningSchedulerCache';
import type { TimelineFilterType } from './scheduler';
import { BRYNTUM_EVENT_FIELDS } from './schedulerConstants';

export const TIMELINE_PRODUCTION_FILTERS: TimelineFilterType[] = [
    {
        displayType: 'CustomComboBoxFacet',
        value: 'publicTitle_production',
        label: 'filter.production.publicTitle',
        name: 'publictitle'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'otherTitles_production',
        label: 'filter.production.otherTitles',
        name: 'otherTitles'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'projectOwnerContactFullName_production',
        label: 'filter.production.projectOwner',
        name: 'projectOwnerContactFullName'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'ownerOrganizationName_production',
        label: 'filter.production.ownerOrganization',
        name: 'ownerOrganizationName'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'productionStatus_production',
        label: 'filter.production.productionStatus',
        name: 'productionStatus'
    },
    {
        displayType: 'CustomListFacet',
        value: 'projectStatus_production',
        label: 'filter.production.projectStatus',
        name: 'projectStatus'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'productionType_production',
        label: 'filter.production.productionType',
        name: 'productiontype'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'productionSubType_production',
        label: 'filter.production.productionSubType',
        name: 'productionsubtype'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'genre_production',
        label: 'filter.genre',
        name: 'genre'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'locality_production',
        label: 'filter.locality',
        name: 'locality'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'adminArea_production',
        label: 'filter.adminArea',
        name: 'adminArea'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'country_production',
        label: 'filter.country',
        name: 'country'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'tags_production',
        label: 'filter.production.tags',
        name: 'tags'
    }
];

const extractNonEmptyStringValues = (values: unknown): string[] => {
    const nonEmptyStringValues = castArray(values).filter(
        (value): value is string => isString(value) && value.trim().length > 0
    );
    return nonEmptyStringValues;
};

// Match the Name.AsString representation indexed by the workspace's owner facet.
const extractProductionOwnerName = (name: search.Name = {}): string => {
    const firstName = name?.firstName ?? '';
    const emailMatch = firstName.match(/^([^@]+)@([^@]+\.[^@]+)$/);
    if (emailMatch) {
        const emailLocalPart = emailMatch[1].trim();
        return emailLocalPart;
    }
    if (name?.lastName === '[last name]') return firstName;

    const lastName = name?.lastName ?? '';
    const indexedOwnerName = getContactNameFromObject({ firstName, lastName });
    return indexedOwnerName;
};

/** Use the workspace's indexed values for both cached facet options and row matching. */
const extractProductionFacetValues = (production: Partial<BryntumProductionRecord>, facetName: string): string[] => {
    let rawFacetValues: unknown;
    switch (facetName) {
        case 'publictitle':
            rawFacetValues = production.name || production.publicTitle;
            break;
        case 'otherTitles':
            rawFacetValues = production.otherTitles?.map((title) => {
                const titleValue = isString(title) ? title : title?.value;
                return titleValue;
            });
            break;
        case 'projectOwnerContactFullName':
            rawFacetValues = production.projectOwnerContacts?.map((contact) => {
                const ownerName = extractProductionOwnerName(contact?.name);
                return ownerName;
            });
            break;
        case 'ownerOrganizationName':
            rawFacetValues = production.ownerOrganizationName;
            break;
        case 'productionStatus':
            rawFacetValues = production.productionStatus;
            break;
        case 'projectStatus':
            rawFacetValues = production.projectStatus;
            break;
        case 'productiontype':
            rawFacetValues = production.productionType;
            break;
        case 'productionsubtype':
            rawFacetValues = production.productionSubType;
            break;
        case 'genre':
            rawFacetValues = production.genre;
            break;
        case 'locality':
            rawFacetValues = production.locations?.map((location) => location?.address?.locality);
            break;
        case 'adminArea':
            rawFacetValues = production.locations?.map((location) => location?.address?.adminArea);
            break;
        case 'country':
            rawFacetValues = production.locations?.map((location) => location?.address?.countryRegionIso2);
            break;
        case 'tags':
            rawFacetValues = production.tags;
            break;
        case BRYNTUM_EVENT_FIELDS.srType:
            rawFacetValues = production['sr-type'];
            break;
    }
    const facetValues = extractNonEmptyStringValues(rawFacetValues);
    return facetValues;
};

const FILTER_BY_IDENTIFIER = new Map(
    TIMELINE_PRODUCTION_FILTERS.map((filter) => {
        const filterEntry: [string, TimelineFilterType] = [filter.value, filter];
        return filterEntry;
    })
);

/** Compile selections once per filter change: OR within a facet, AND across facets. */
export const makeProductionFilterPredicate = (filters: Pick<search.SkFiltersSet, 'identifier' | 'value'>[]) => {
    const selectedValues = new Map<string, Set<string>>();
    filters.forEach((filter) => {
        const facetName =
            filter.identifier === BRYNTUM_EVENT_FIELDS.srType
                ? filter.identifier
                : FILTER_BY_IDENTIFIER.get(filter.identifier)?.name;
        if (facetName && isString(filter.value)) {
            const facetValues = selectedValues.get(facetName) ?? new Set<string>();
            facetValues.add(filter.value);
            selectedValues.set(facetName, facetValues);
        }
    });
    const selectedFacets = Array.from(selectedValues);
    const matchesProductionFilters = (production: Partial<BryntumProductionRecord>): boolean => {
        const matchesAllFacets = selectedFacets.every(([facetName, values]) => {
            const facetValues = extractProductionFacetValues(production, facetName);
            const matchesSelectedValue = facetValues.some((value) => values.has(value));
            return matchesSelectedValue;
        });
        return matchesAllFacets;
    };
    return matchesProductionFilters;
};

const constructProductionFilterData = (facets: search.SkFacetSet[], total = 0) => {
    const productionFilterData = {
        results: {
            facets,
            hits: { page: { total } },
            summary: {}
        }
    };
    return productionFilterData;
};

export const getProductionFilterDataFromFacets = (
    facets: search.SkFacetSet[] = [],
    timelineFilters: TimelineFilterType[],
    t: I18nFunctionType
) => {
    const productionFacets = timelineFilters.flatMap((filter) => {
        const facet = facets.find((candidate) => candidate?.identifier === filter.name);
        if (facet) {
            const adaptedFacets: search.SkFacetSet[] = [
                {
                    identifier: filter.value,
                    type: 'RefinementSelectFacet',
                    label: t(filter.label),
                    display: filter.displayType,
                    entries: (facet.entries || []).filter(Boolean).map((entry) => {
                        const facetEntry = { ...entry };
                        return facetEntry;
                    })
                }
            ];
            return adaptedFacets;
        }
        const missingFacets: search.SkFacetSet[] = [];
        return missingFacets;
    });
    const productionFilterData = constructProductionFilterData(productionFacets);
    return productionFilterData;
};

export const getFilterDataFromProductions = (
    records: Array<BryntumProductionRecord | BryntumResourceRecord> = [],
    timelineFilters: TimelineFilterType[],
    t: I18nFunctionType
) => {
    const productions = records.filter(
        (record): record is BryntumProductionRecord =>
            record?.[BRYNTUM_EVENT_FIELDS.srType] === ENTITY_CONSTANTS.production
    );
    const productionFacets = timelineFilters.map((filter) => {
        const counts = new Map<string, number>();
        productions.forEach((production) => {
            new Set(extractProductionFacetValues(production, filter.name)).forEach((value) => {
                counts.set(value, (counts.get(value) || 0) + 1);
            });
        });
        const entries = Array.from(counts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([label, count]) => {
                const facetEntry: search.SkFacetSetEntry = {
                    id: `${filter.name}_${label}`,
                    count,
                    label,
                    __typename: 'SKFacetSetEntry'
                };
                return facetEntry;
            });
        const productionFacet: search.SkFacetSet = {
            identifier: filter.value,
            type: 'RefinementSelectFacet',
            label: t(filter.label),
            display: filter.displayType,
            entries
        };
        return productionFacet;
    });
    const productionFilterData = constructProductionFilterData(productionFacets, productions.length);
    return productionFilterData;
};
